@import 'tailwindcss';

@source '../../../../../../index.html';
@source '../../../../../**/*.{js,ts,html}';
@source '../../../../../../.storybook/**/*.{js,ts,mjs}';

@theme {
  --color-primary: var(--color-primary);
  --color-secondary: var(--color-secondary);
  --color-background: var(--color-background);
  --color-text: var(--color-text);
  /* Radios Tailwind: máximo 6px (incl. rounded-full vía --radius-full) */
  --radius-md: 6px;
  --radius-lg: 6px;
  --radius-xl: 6px;
  --radius-2xl: 6px;
  --radius-3xl: 6px;
  --radius-4xl: 6px;
  --radius-full: 6px;
}

/**
 * Identidad Alcaldía de Medellín (manual): policromía del escudo — azul cielo, ocre,
 * grises (nubes), dorado; tipografía y contornos en negro; bandera — blanco y verde (ciudad).
 * Material 3: tokens semánticos derivados; --color-primary/--color-secondary siguen
 * sobrecargables por build y `applyTenantCssVariables`.
 */
:root {
  /* —— Anclas de marca —— */
  --alcaldia-sky-900: #0d3d66;
  --alcaldia-sky-800: #155a9e;
  /* Azul cielo (fondo escudo / acción principal) */
  --alcaldia-sky-700: #1a6fbf;
  --alcaldia-sky-600: #2d82d3;
  --alcaldia-sky-100: #d6ebfc;
  --alcaldia-sky-50: #f0f7fd;

  /* Ocre (torre) */
  --alcaldia-ochre-700: #6b4e2e;
  --alcaldia-ochre-500: #a67c52;
  --alcaldia-ochre-100: #f0e8dc;

  /* Grises nubes / sobrios */
  --alcaldia-mist-600: #5c6370;
  --alcaldia-mist-500: #7d8694;
  --alcaldia-mist-200: #d8dee6;
  --alcaldia-mist-100: #eef1f5;

  /* Dorado institucional */
  --alcaldia-gold-700: #8a6b1f;
  --alcaldia-gold-500: #c9a227;
  --alcaldia-gold-100: #faf3dd;

  /* Verde bandera (integridad / ciudad) */
  --alcaldia-flag-green-800: #0a4a32;
  --alcaldia-flag-green-700: #0d6b47;
  --alcaldia-flag-green-600: #15803d;
  --alcaldia-flag-green-100: #d9f3e5;

  /* Negro estándar: texto y contornos */
  --alcaldia-ink: #0a0a0a;
  --alcaldia-ink-muted: #2d3238;

  --alcaldia-white: #ffffff;

  /* Primario / secundario por defecto (tenant / applyTenantCssVariables pueden reemplazar) */
  --color-primary: var(--alcaldia-sky-700);
  --color-secondary: var(--alcaldia-flag-green-700);

  --md-sys-color-primary: var(--color-primary);
  --md-sys-color-on-primary: var(--alcaldia-white);
  --md-sys-color-primary-container: color-mix(in srgb, var(--color-primary) 16%, var(--alcaldia-sky-50));
  --md-sys-color-on-primary-container: var(--alcaldia-sky-900);

  --md-sys-color-secondary: var(--color-secondary);
  --md-sys-color-on-secondary: var(--alcaldia-white);
  --md-sys-color-secondary-container: var(--alcaldia-flag-green-100);
  --md-sys-color-on-secondary-container: var(--alcaldia-flag-green-800);

  --md-sys-color-tertiary: var(--alcaldia-gold-500);
  --md-sys-color-on-tertiary: var(--alcaldia-ink);
  --md-sys-color-tertiary-container: var(--alcaldia-gold-100);
  --md-sys-color-on-tertiary-container: var(--alcaldia-gold-700);

  /* Superficies M3 (tonal steps, alineados a roles de Android / Material 3) */
  --md-sys-color-surface: var(--alcaldia-sky-50);
  --md-sys-color-surface-dim: color-mix(in srgb, var(--alcaldia-sky-600) 6%, var(--alcaldia-white));
  --md-sys-color-surface-container-low: color-mix(in srgb, var(--alcaldia-sky-600) 4%, var(--alcaldia-white));
  --md-sys-color-surface-container: color-mix(in srgb, var(--alcaldia-sky-600) 8%, var(--alcaldia-white));
  --md-sys-color-surface-container-high: color-mix(in srgb, var(--alcaldia-sky-600) 10%, var(--alcaldia-white));
  --md-sys-color-surface-container-highest: var(--alcaldia-mist-100);

  --md-sys-color-on-surface: var(--alcaldia-ink);
  --md-sys-color-on-surface-variant: var(--alcaldia-mist-600);
  --md-sys-color-outline: var(--alcaldia-mist-500);
  --md-sys-color-outline-variant: var(--alcaldia-mist-200);

  --md-sys-color-error: #ba1a1a;
  --md-sys-color-on-error: var(--alcaldia-white);
  --md-sys-color-error-container: #ffdad6;
  --md-sys-color-on-error-container: #410002;

  --md-elevation-1: 0 1px 2px rgba(10, 10, 10, 0.06), 0 1px 3px 1px rgba(26, 111, 191, 0.05);
  --md-elevation-2: 0 1px 2px rgba(10, 10, 10, 0.05), 0 2px 6px 2px rgba(26, 111, 191, 0.06);
  --md-elevation-3: 0 4px 8px 3px rgba(10, 10, 10, 0.07), 0 1px 3px rgba(26, 111, 191, 0.06);

  /* Todos los radios de componente: máx. 6px (incl. chips / pills) */
  --md-shape-corner-extra-large: 6px;
  --md-shape-corner-large: 6px;
  --md-shape-corner-medium: 6px;
  --md-shape-corner-full: 6px;

  --md-state-hover-opacity: 0.08;
  --md-state-pressed-opacity: 0.12;
  --md-state-focus-opacity: 0.12;

  /* Motion M3 (duración / curva estándar de componentes en Android) */
  --md-motion-duration-short2: 200ms;
  --md-motion-duration-short4: 250ms;
  --md-motion-easing-standard: cubic-bezier(0.2, 0, 0, 1);
  --md-motion-easing-emphasized: cubic-bezier(0.2, 0, 0, 1);
}

/* Scroll: menos “rebote” tipo navegador; más cercano a vistas nativas */
main#main,
.layout-main-scroll {
  overscroll-behavior-y: contain;
}

body {
  margin: 0;
  min-height: 100dvh;
  font-family:
    Roboto,
    RobotoDraft,
    'Google Sans',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  background-color: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
}

.md-sys-ripple {
  position: relative;
  overflow: hidden;
}

.md-sys-ripple::after {
  content: '';
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard);
  pointer-events: none;
  border-radius: inherit;
}

.md-sys-ripple:hover::after {
  opacity: var(--md-state-hover-opacity);
}

.md-sys-ripple:active::after {
  opacity: var(--md-state-pressed-opacity);
}

/**
 * Shell: Navigation Rail / Navigation Bar (M3 destination).
 * Icono en contenedor con primary-container al activo; etiqueta label-medium.
 * Barra inferior: indicador superior (pill) animado al cambiar de destino.
 */
.shell-rail-nav a,
.shell-bottom-nav a {
  position: relative;
}

.shell-rail-nav a .shell-nav-dest-icon-shell,
.shell-bottom-nav a .shell-nav-dest-icon-shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--md-shape-corner-medium);
  transition:
    background-color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    color var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized);
}

.shell-rail-nav a[aria-current='page'] .shell-nav-dest-icon-shell,
.shell-bottom-nav a[aria-current='page'] .shell-nav-dest-icon-shell {
  background-color: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  transform: scale(1.04);
}

.shell-rail-nav a:not([aria-current='page']) .shell-nav-dest-icon-shell,
.shell-bottom-nav a:not([aria-current='page']) .shell-nav-dest-icon-shell {
  color: var(--md-sys-color-on-surface-variant);
}

.shell-rail-nav a .shell-nav-dest-label,
.shell-bottom-nav a .shell-nav-dest-label {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  letter-spacing: 0.00625em;
  transition: color var(--md-motion-duration-short2) var(--md-motion-easing-standard);
}

.shell-rail-nav a[aria-current='page'] .shell-nav-dest-label,
.shell-bottom-nav a[aria-current='page'] .shell-nav-dest-label {
  color: var(--md-sys-color-on-primary-container);
}

.shell-rail-nav a:not([aria-current='page']) .shell-nav-dest-label,
.shell-bottom-nav a:not([aria-current='page']) .shell-nav-dest-label {
  color: var(--md-sys-color-on-surface-variant);
}

.shell-bottom-nav a.md-sys-ripple::after {
  z-index: 0;
}

.shell-bottom-nav a::before {
  content: '';
  position: absolute;
  z-index: 1;
  top: 0;
  left: 50%;
  width: 1.5rem;
  height: 3px;
  border-radius: var(--md-shape-corner-medium);
  background-color: var(--md-sys-color-primary);
  transform: translateX(-50%) scaleX(0.2);
  opacity: 0;
  transition:
    opacity var(--md-motion-duration-short2) var(--md-motion-easing-standard),
    transform var(--md-motion-duration-short4) var(--md-motion-easing-emphasized);
  pointer-events: none;
}

.shell-bottom-nav a[aria-current='page']::before {
  transform: translateX(-50%) scaleX(1);
  opacity: 1;
}

.shell-pending-sync-badge {
  position: absolute;
  top: -0.2rem;
  right: -0.35rem;
  z-index: 2;
  display: flex;
  min-width: 1.125rem;
  height: 1.125rem;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background-color: var(--md-sys-color-error);
  color: var(--md-sys-color-on-error);
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--md-sys-color-shadow, #000) 28%, transparent);
  pointer-events: none;
}

/* Snackbar (Material / Android); usado por DomToastNotificationAdapter */
.md-snackbar {
  position: fixed;
  left: max(16px, env(safe-area-inset-left));
  right: max(16px, env(safe-area-inset-right));
  bottom: max(16px, env(safe-area-inset-bottom));
  z-index: 10000;
  display: flex;
  justify-content: center;
  pointer-events: none;
  transform: translateY(calc(100% + 24px));
  opacity: 0;
  transition:
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease;
}

.md-snackbar.md-snackbar--visible {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .md-snackbar {
    transition: opacity 0.15s ease;
  }

  .md-snackbar:not(.md-snackbar--visible) {
    transform: translateY(0);
  }
}

.md-snackbar__surface {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: min(560px, 100%);
  padding: 0.875rem 1rem;
  border-radius: var(--md-shape-corner-medium);
  box-shadow: var(--md-elevation-3);
  background: var(--md-sys-color-surface-container-highest);
  color: var(--md-sys-color-on-surface);
  border: 1px solid var(--md-sys-color-outline-variant);
}

.md-snackbar__surface--success {
  border-color: color-mix(in srgb, var(--md-sys-color-secondary) 38%, var(--md-sys-color-outline-variant));
}

.md-snackbar__surface--error {
  border-color: color-mix(in srgb, var(--md-sys-color-error) 45%, var(--md-sys-color-outline-variant));
  background: var(--md-sys-color-error-container);
  color: var(--md-sys-color-on-error-container);
}

.md-snackbar__surface--warning {
  border-color: color-mix(in srgb, var(--alcaldia-ochre-500) 45%, var(--md-sys-color-outline-variant));
}

.md-snackbar__surface--info {
  border-color: color-mix(in srgb, var(--md-sys-color-primary) 35%, var(--md-sys-color-outline-variant));
}

.md-snackbar__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--md-sys-color-primary);
}

.md-snackbar__surface--success .md-snackbar__icon {
  color: var(--md-sys-color-secondary);
}

.md-snackbar__surface--error .md-snackbar__icon {
  color: var(--md-sys-color-error);
}

.md-snackbar__surface--warning .md-snackbar__icon {
  color: var(--alcaldia-ochre-700);
}

.md-snackbar__text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.4;
  font-weight: 400;
}

/* WCAG 2.1 — foco visible (contraste con negro institucional) */
:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

button:focus:not(:focus-visible),
a:focus:not(:focus-visible) {
  outline: none;
}
